"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, CircleCheck as CheckCircle2, Phone, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
export default function Hero() {
  const shouldReduceMotion = useReducedMotion();
  const transitionConfig = {
    duration: shouldReduceMotion ? 0 : 0.4,
    ease: [0.16, 1, 0.3, 1]
  };
  const fadeInUp = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0
    }
  };
  const perks = ["Curbless stone walk-ins", "Custom oak vanities", "Clear project scope"];
  return <section id="hero" data-nav="light" className="relative overflow-hidden bg-background pt-28 pb-16 sm:pt-36 sm:pb-24 lg:pt-40 lg:pb-32">
      {/* Subtle architectural ambient background grid accent */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.03] [background-image:radial-gradient(currentColor_1px,transparent_1px)] [background-size:24px_24px] text-foreground" />

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12 lg:gap-10">
          {/* Left Column: Editorial & Conversion Narrative */}
          <motion.div initial="hidden" animate="visible" transition={{
          staggerChildren: shouldReduceMotion ? 0 : 0.08
        }} className="lg:col-span-7 flex flex-col items-start">
            {/* Eyebrow Label */}
            <motion.div variants={fadeInUp} transition={transitionConfig}>
              <Badge variant="outline" className="mb-4 rounded-full border-border bg-secondary px-3 py-1 text-xs font-semibold tracking-wider text-foreground uppercase">
                <Sparkles className="mr-1.5 size-3.5 text-chart-1" />
                Bespoke Residential Renovations
              </Badge>
            </motion.div>

            {/* Main Headline with Architectural Copper Hairline */}
            <motion.div variants={fadeInUp} transition={transitionConfig} className="relative mb-6">
              <div aria-hidden="true" className="mb-3 h-[2px] w-14 bg-chart-1" />
              <h1 className="font-serif text-3xl font-semibold tracking-tight text-foreground sm:text-5xl lg:text-6xl lg:leading-[1.1]">
                Bathroom Remodeling in Decatur, Georgia
              </h1>
            </motion.div>

            {/* Lead Narrative */}
            <motion.p variants={fadeInUp} transition={transitionConfig} className="max-w-2xl text-base leading-relaxed text-muted-foreground sm:text-lg lg:text-xl">
              We craft tailored master retreats, bespoke vanities, and light-filled walk-in showers built for daily comfort. Rooted in Decatur with meticulous attention to detail and honest project planning.
            </motion.p>

            {/* Value Highlights */}
            <motion.div variants={fadeInUp} transition={transitionConfig} className="mt-6 flex flex-wrap items-center gap-y-2 gap-x-6 text-sm text-foreground font-medium">
              {perks.map((perk, i) => <div key={perk} data-index={i} className="flex items-center gap-2">
                  <CheckCircle2 className="size-4 text-chart-1 shrink-0" />
                  <span>{perk}</span>
                </div>)}
            </motion.div>

            {/* CTAs */}
            <motion.div variants={fadeInUp} transition={transitionConfig} className="mt-8 flex flex-wrap items-center gap-4 sm:gap-5">
              <Button asChild size="lg" className="rounded-md bg-chart-1 text-primary-foreground font-semibold shadow-md transition-[color,background-color,box-shadow,transform] hover:opacity-95 active:scale-[0.98]">
                <Link to="#" className="inline-flex items-center gap-2">
                  <span>Request an estimate</span>
                  <ArrowRight className="size-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="rounded-md border-border bg-card/80 text-foreground font-medium shadow-xs transition-[color,background-color,border-color] hover:bg-secondary">
                <a href="tel:+14708479076" className="inline-flex items-center gap-2">
                  <Phone className="size-4 text-muted-foreground" />
                  <span>(470) 847-9076</span>
                </a>
              </Button>
            </motion.div>
          </motion.div>

          {/* Right Column: Visual Showcase & Frosted Glass Layer */}
          <motion.div initial={{
          opacity: 0,
          y: shouldReduceMotion ? 0 : 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          ...transitionConfig,
          delay: shouldReduceMotion ? 0 : 0.15
        }} className="lg:col-span-5 relative">
            {/* Primary Visual Container */}
            <div className="relative mx-auto max-w-md lg:max-w-none">
              {/* Back Accent Border Frame */}
              <div aria-hidden="true" className="absolute -inset-2.5 rounded-2xl border border-border/80 bg-secondary/30 pointer-events-none hidden sm:block" />

              {/* Main Image */}
              <div className="relative overflow-hidden rounded-xl border border-border bg-card shadow-lg">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/decatur_master_bathroom_remodel.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/decatur_master_bathroom_remodel.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821937/img/decatur_master_bathroom_remodel.webp-wvc-sizes" alt="Finished luxury master bathroom remodel in Decatur featuring custom quartz vanity and freestanding soaking tub" loading="eager" className="aspect-4/5 w-full object-cover object-center" />

                {/* Subtle dark gradient overlay for text readability at bottom */}
                <div aria-hidden="true" className="absolute inset-0 bg-gradient-to-t from-foreground/40 via-transparent to-transparent opacity-60" />
              </div>

              {/* Dimensional Frosted Glass Detail Card */}
              <motion.div initial={{
              opacity: 0,
              y: shouldReduceMotion ? 0 : 12
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              ...transitionConfig,
              delay: shouldReduceMotion ? 0 : 0.25
            }} className="sm:absolute sm:-bottom-6 sm:left-6 sm:right-6 mt-4 sm:mt-0 rounded-xl border border-border bg-card/90 p-4 sm:p-5 shadow-xl backdrop-blur-md">
                <div>
                  <span className="text-[11px] font-bold tracking-widest text-muted-foreground uppercase">
                    Decatur Project Showcase
                  </span>
                  <h2 className="mt-1 font-serif text-base font-semibold text-card-foreground sm:text-lg">
                    Master Bathroom Renovation
                  </h2>
                  <p className="mt-0.5 text-xs text-muted-foreground sm:text-sm">
                    Solid walnut double vanity, brushed brass, & curbless zellige shower
                  </p>
                </div>

                <div className="mt-3 flex items-center justify-between border-t border-border/80 pt-3 text-xs">
                  <span className="text-muted-foreground">Timeline: 4 Weeks</span>
                  <Link to="#" className="font-semibold text-chart-1 hover:underline inline-flex items-center gap-1">
                    View details
                    <ArrowRight className="size-3" />
                  </Link>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}